import type { IMonthFacts, IPeriodFacts } from '../types/metrics';
import type { MonthKey } from '../types/period';
import type {
  ColumnHeaderId,
  IComponentFacts,
  ReportComponentId,
} from '../types/report';
import { MetricsHelper } from './MetricsHelper';
import { MonthKeyHelper } from './MonthKeyHelper';
import { PeriodHelper } from './PeriodHelper';
import { ReportFormatHelper } from './ReportFormatHelper';

export interface IColumnHeaderRow {
  label: string;
  value: string;
  /** Plain-language definition, shown as a tooltip on screen and a footnote in exports. */
  definition: string;
}

export interface ISparklinePoint {
  month: MonthKey;
  value: number | null;
}

export interface IColumnHeaderChange {
  text: string;
  /** null when there is no movement to judge. */
  favourable: boolean | null;
}

export interface IColumnHeaderModel {
  label: string;
  value: string;
  change: IColumnHeaderChange;
  rows: IColumnHeaderRow[];
  sparkline: ISparklinePoint[];
  /** Compact labels for the first and last sparkline points. */
  sparklineStart: string;
  sparklineEnd: string;
}

interface IChangeSpec {
  /** A positive delta is good news. */
  upIsGood: boolean;
  /** Names for a value that flips sign or reads better as a magnitude (e.g. loss, burn). */
  noun?: { positive: string; negative: string };
}

interface IColumnSpec {
  label: string;
  value: (facts: IPeriodFacts) => number | null;
  monthly: (facts: IMonthFacts) => number | null;
  change: IChangeSpec;
  rows: (facts: IComponentFacts) => IColumnHeaderRow[];
}

const SPARKLINE_MONTHS = 12;

const compact = (value: number | null, currency: string) =>
  ReportFormatHelper.currency(value, currency, { compact: true });

const COLUMN_BY_COMPONENT: Partial<Record<ReportComponentId, ColumnHeaderId>> =
  {
    cash_column: 'cash',
    profit_column: 'profit',
    revenue_column: 'revenue',
  };

/** Headline, change copy, supporting rows and sparkline for the column header components. */
export class ColumnHeaderHelper {
  static readonly DEFINITIONS = {
    netBurn: 'Cash used by operations and investing',
    runway: 'Cash divided by trailing 3-month average burn',
    grossMargin: 'Gross profit as a share of revenue',
    netMargin: 'Net profit after tax as a share of revenue',
    trailing12: 'Revenue over the 12 months ending this period',
    priorTrailing12: 'Revenue over the 12 months before those',
  } as const;

  private static readonly SPECS: Record<ColumnHeaderId, IColumnSpec> = {
    cash: {
      label: 'Cash + investments',
      value: (f) => f.balance.cashAndInvestments,
      monthly: (m) => m.balance.cashAndInvestments,
      change: { upIsGood: true },
      rows: ({ current, currency }) => [
        {
          label: 'Net burn',
          value: current.cashFlow.hasOpening
            ? compact(current.cashFlow.netBurn, currency)
            : ReportFormatHelper.number(null),
          definition: ColumnHeaderHelper.DEFINITIONS.netBurn,
        },
        {
          label: 'Runway',
          value: ReportFormatHelper.months(current.runwayMonths),
          definition: ColumnHeaderHelper.DEFINITIONS.runway,
        },
      ],
    },
    profit: {
      label: 'Net profit',
      value: (f) => f.pnl.netProfit,
      monthly: (m) => m.pnl.netProfit,
      change: {
        upIsGood: true,
        noun: { positive: 'Profit', negative: 'Loss' },
      },
      rows: ({ current }) => [
        {
          label: 'Gross margin',
          value: ReportFormatHelper.percent(current.pnl.grossMargin),
          definition: ColumnHeaderHelper.DEFINITIONS.grossMargin,
        },
        {
          label: 'Net margin',
          value: ReportFormatHelper.percent(current.pnl.netMargin),
          definition: ColumnHeaderHelper.DEFINITIONS.netMargin,
        },
      ],
    },
    revenue: {
      label: 'Revenue',
      value: (f) => f.pnl.revenue,
      monthly: (m) => m.pnl.revenue,
      change: { upIsGood: true },
      rows: ({ table, resolved, currency }) => {
        const last = resolved.months[resolved.months.length - 1];
        const trailing = last
          ? MetricsHelper.aggregate(
              table,
              MonthKeyHelper.range(MonthKeyHelper.add(last, -11), last)
            )
          : null;
        const prior = last
          ? MetricsHelper.aggregate(
              table,
              MonthKeyHelper.range(
                MonthKeyHelper.add(last, -23),
                MonthKeyHelper.add(last, -12)
              )
            )
          : null;
        return [
          {
            label: 'Trailing 12 months',
            value: compact(trailing?.pnl.revenue ?? null, currency),
            definition: ColumnHeaderHelper.DEFINITIONS.trailing12,
          },
          {
            label: 'Prior 12 months',
            value:
              prior && prior.presentMonths.length > 0
                ? compact(prior.pnl.revenue, currency)
                : ReportFormatHelper.number(null),
            definition: ColumnHeaderHelper.DEFINITIONS.priorTrailing12,
          },
        ];
      },
    },
  };

  static columnIdFor(componentId: ReportComponentId): ColumnHeaderId | null {
    return COLUMN_BY_COMPONENT[componentId] ?? null;
  }

  static model(id: ColumnHeaderId, facts: IComponentFacts): IColumnHeaderModel {
    const spec = ColumnHeaderHelper.SPECS[id];
    const { current, comparison, resolved, table } = facts;
    const months = ColumnHeaderHelper.sparklineMonths(facts);
    return ColumnHeaderHelper.build(
      spec,
      spec.value(current),
      comparison ? spec.value(comparison) : null,
      spec.rows(facts),
      MetricsHelper.monthlySeries(table, months, spec.monthly),
      resolved.comparisonLabel,
      facts.currency
    );
  }

  private static sparklineMonths(facts: IComponentFacts): MonthKey[] {
    return PeriodHelper.trendMonths(facts.resolved, SPARKLINE_MONTHS).slice(
      -SPARKLINE_MONTHS
    );
  }

  private static build(
    spec: IColumnSpec,
    value: number | null,
    prior: number | null,
    rows: IColumnHeaderRow[],
    sparkline: ISparklinePoint[],
    comparisonLabel: string,
    currency: string
  ): IColumnHeaderModel {
    const present = sparkline.filter((p) => p.value !== null);
    const first = present[0];
    const lastPoint = present[present.length - 1];

    return {
      label: spec.label,
      value: compact(value, currency),
      change: ColumnHeaderHelper.change(
        spec.change,
        value,
        prior,
        currency,
        comparisonLabel
      ),
      rows,
      sparkline,
      sparklineStart: first ? compact(first.value, currency) : '',
      sparklineEnd: lastPoint ? compact(lastPoint.value, currency) : '',
    };
  }

  /**
   * Plain-text change with the direction spelled out. When a value is negative
   * (a loss) the sign of the delta misleads, so the magnitude noun carries the
   * meaning instead: "Loss down $41K vs Jul 2026".
   */
  static change(
    spec: IChangeSpec,
    value: number | null,
    prior: number | null,
    currency: string,
    comparisonLabel: string
  ): IColumnHeaderChange {
    if (value === null || prior === null) {
      return { text: 'No comparison', favourable: null };
    }
    const delta = value - prior;
    if (delta === 0) {
      return { text: `Unchanged ${comparisonLabel}`, favourable: null };
    }
    const favourable = delta > 0 === spec.upIsGood;
    const amount = compact(Math.abs(delta), currency);
    if (!spec.noun) {
      return {
        text: `${delta > 0 ? 'Up' : 'Down'} ${amount} ${comparisonLabel}`,
        favourable,
      };
    }
    // Both values on the negative side: describe the magnitude of the loss, whose
    // direction is the opposite of the delta's sign.
    const asNegative = value < 0 && prior <= 0;
    const noun = asNegative ? spec.noun.negative : spec.noun.positive;
    const direction = delta > 0 !== asNegative ? 'up' : 'down';
    return {
      text: `${noun} ${direction} ${amount} ${comparisonLabel}`,
      favourable,
    };
  }
}
